import { createContext, useContext, useState, useRef, useCallback, useEffect, type ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTimer, getRemainingSeconds, formatTime, type TimerState } from '../hooks/useTimer';

// ── Context ──

interface TimerContextType {
  startTimer: (id: string, seconds: number, label: string, recipeId?: number, stepNumber?: number) => void;
  pauseTimer: (id: string) => void;
  resumeTimer: (id: string) => void;
  stopTimer: (id: string) => void;
  adjustTimer: (id: string, deltaMs: number) => void;
  acknowledgeTimer: (id: string) => void;
  activeTimers: TimerState[];
  timerById: (id: string) => TimerState | undefined;
}

const TimerContext = createContext<TimerContextType | null>(null);

export function useGlobalTimer() {
  const ctx = useContext(TimerContext);
  if (!ctx) throw new Error('useGlobalTimer must be used within TimerProvider');
  return ctx;
}

export function TimerProvider({ children }: { children: ReactNode }) {
  const timer = useTimer();
  return (
    <TimerContext.Provider value={timer}>
      {children}
    </TimerContext.Provider>
  );
}

// ── Global Timer Overlay (Draggable) ──

export function GlobalTimerOverlay() {
  const { activeTimers, pauseTimer, resumeTimer, stopTimer, adjustTimer, acknowledgeTimer } = useGlobalTimer();
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);

  // ── Draggable via handle only ──
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null); // null = default position
  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const posStart = useRef({ x: 0, y: 0 });

  const getDefaultPos = useCallback(() => ({
    x: 12,
    y: window.innerHeight - 176, // above bottom nav
  }), []);

  // Reset on route change
  useEffect(() => {
    setExpanded(false);
    setPos(null);
  }, [location.pathname]);

  const clamp = useCallback((x: number, y: number) => ({
    x: Math.max(0, Math.min(window.innerWidth - 200, x)),
    y: Math.max(0, Math.min(window.innerHeight - 100, y)),
  }), []);

  // Drag handle events — completely separate from tap
  const onHandleTouchStart = useCallback((e: React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const t = e.touches[0];
    const current = pos || getDefaultPos();
    isDragging.current = true;
    dragStart.current = { x: t.clientX, y: t.clientY };
    posStart.current = current;
  }, [pos, getDefaultPos]);

  const onHandleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging.current) return;
    const t = e.touches[0];
    const dx = t.clientX - dragStart.current.x;
    const dy = t.clientY - dragStart.current.y;
    setPos(clamp(posStart.current.x + dx, posStart.current.y + dy));
  }, [clamp]);

  const onHandleTouchEnd = useCallback(() => {
    isDragging.current = false;
  }, []);

  const onHandleMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const current = pos || getDefaultPos();
    isDragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY };
    posStart.current = current;

    const onMove = (ev: MouseEvent) => {
      const dx = ev.clientX - dragStart.current.x;
      const dy = ev.clientY - dragStart.current.y;
      setPos(clamp(posStart.current.x + dx, posStart.current.y + dy));
    };
    const onUp = () => {
      isDragging.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [pos, getDefaultPos, clamp]);

  if (activeTimers.length === 0) return null;

  const runningOrPaused = activeTimers.filter(t => t.status !== 'finished');
  const finished = activeTimers.filter(t => t.status === 'finished');
  const shortest = runningOrPaused.length > 0
    ? runningOrPaused.reduce((a, b) => getRemainingSeconds(a) < getRemainingSeconds(b) ? a : b)
    : null;

  const currentPos = pos || getDefaultPos();

  return (
    <>
      {/* Backdrop when expanded */}
      {expanded && (
        <div className="fixed inset-0 bg-black/20 z-[39]" onClick={() => setExpanded(false)} />
      )}

      {/* ── Compact Bar ── */}
      {!expanded && (
        <div
          className="fixed z-40"
          style={{ left: currentPos.x, top: currentPos.y, width: 'calc(100vw - 24px)', maxWidth: '420px' }}
        >
          <div className={`flex items-center rounded-2xl shadow-lg ${
            finished.length > 0 ? 'bg-red-500 text-white animate-timer-pulse' : 'bg-white border-2 border-emerald-400'
          }`}>
            {/* Drag handle — left side */}
            <div
              onTouchStart={onHandleTouchStart}
              onTouchMove={onHandleTouchMove}
              onTouchEnd={onHandleTouchEnd}
              onMouseDown={onHandleMouseDown}
              className={`shrink-0 px-2 py-4 cursor-grab active:cursor-grabbing touch-none flex flex-col items-center justify-center gap-0.5 ${
                finished.length > 0 ? 'text-white/40' : 'text-gray-300'
              }`}
            >
              <div className="w-4 h-0.5 rounded-full bg-current" />
              <div className="w-4 h-0.5 rounded-full bg-current" />
              <div className="w-4 h-0.5 rounded-full bg-current" />
            </div>

            {/* Tap area — opens expanded view */}
            <button
              onClick={() => setExpanded(true)}
              className="flex-1 flex items-center gap-3 py-3 pr-4 min-w-0"
            >
              {finished.length > 0 ? (
                <>
                  <span className="text-lg">🔔</span>
                  <span className="flex-1 text-left text-sm font-semibold truncate">
                    {finished.length === 1 ? `${finished[0].label} — Fertig!` : `${finished.length} Timer abgelaufen!`}
                  </span>
                </>
              ) : (
                <>
                  <span className="text-lg">🕐</span>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-xs text-gray-500">{activeTimers.length} Timer aktiv</p>
                    <p className="text-lg font-bold text-emerald-600 tabular-nums">
                      {shortest ? formatTime(getRemainingSeconds(shortest)) : '--:--'}
                    </p>
                  </div>
                  {runningOrPaused.length > 1 && (
                    <div className="flex gap-1.5">
                      {runningOrPaused.slice(1, 3).map(t => (
                        <span key={t.id} className="text-xs text-gray-400 tabular-nums">{formatTime(getRemainingSeconds(t))}</span>
                      ))}
                    </div>
                  )}
                </>
              )}
              <svg className={`w-4 h-4 shrink-0 ${finished.length > 0 ? 'text-white/50' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* ── Expanded View (fixed at bottom) ── */}
      {expanded && (
        <div className="fixed bottom-24 md:bottom-4 left-3 right-3 z-40">
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                <span className="text-sm font-semibold text-gray-700">Timer ({activeTimers.length})</span>
                <button onClick={() => setExpanded(false)} className="p-1 text-gray-400 hover:text-gray-600">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
              <div className="max-h-80 overflow-y-auto divide-y divide-gray-100">
                {activeTimers.map(timer => (
                  <TimerCard
                    key={timer.id}
                    timer={timer}
                    onPause={() => pauseTimer(timer.id)}
                    onResume={() => resumeTimer(timer.id)}
                    onStop={() => stopTimer(timer.id)}
                    onAdjust={(delta) => adjustTimer(timer.id, delta)}
                    onAcknowledge={() => acknowledgeTimer(timer.id)}
                    onNavigate={() => {
                      if (timer.recipeId) {
                        setExpanded(false);
                        navigate(`/recipes/${timer.recipeId}${timer.stepNumber ? `#step-${timer.stepNumber}` : ''}`);
                      }
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Timer Card ──

function TimerCard({ timer, onPause, onResume, onStop, onAdjust, onAcknowledge, onNavigate }: {
  timer: TimerState;
  onPause: () => void;
  onResume: () => void;
  onStop: () => void;
  onAdjust: (deltaMs: number) => void;
  onAcknowledge: () => void;
  onNavigate: () => void;
}) {
  const remaining = getRemainingSeconds(timer);
  const progress = timer.durationMs > 0 ? Math.max(0, 1 - (remaining * 1000) / timer.durationMs) : 1;
  const isFinished = timer.status === 'finished';
  const isPaused = timer.status === 'paused';

  return (
    <div className={`px-4 py-3 ${isFinished ? 'bg-red-50' : ''}`}>
      {/* Label + time */}
      <div className="flex items-center justify-between mb-2">
        <button onClick={onNavigate} className="text-sm font-medium text-gray-700 truncate text-left flex-1 min-w-0 hover:text-emerald-600">
          {timer.label}
        </button>
        <span className={`text-lg font-bold tabular-nums ml-2 ${isFinished ? 'text-red-500' : isPaused ? 'text-amber-500' : 'text-emerald-600'}`}>
          {isFinished ? 'Fertig!' : formatTime(remaining)}
        </span>
      </div>

      {/* Progress bar */}
      {!isFinished && (
        <div className="w-full bg-gray-100 rounded-full h-1.5 mb-2.5">
          <div
            className={`h-1.5 rounded-full transition-all duration-1000 ${isPaused ? 'bg-amber-400' : 'bg-emerald-500'}`}
            style={{ width: `${progress * 100}%` }}
          />
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center gap-1.5">
        {isFinished ? (
          <button onClick={onAcknowledge} className="flex-1 py-2 bg-emerald-500 text-white rounded-xl text-sm font-medium hover:bg-emerald-600 transition-colors">
            Bestätigen
          </button>
        ) : (
          <>
            {timer.status === 'running' ? (
              <button onClick={onPause} className="w-10 h-10 flex items-center justify-center rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200" title="Pause">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/></svg>
              </button>
            ) : (
              <button onClick={onResume} className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-100 text-amber-700 hover:bg-amber-200" title="Fortsetzen">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
              </button>
            )}

            <button onClick={() => onAdjust(-60000)} disabled={remaining <= 60} className="h-10 px-2.5 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs font-medium disabled:opacity-30" title="-1 Min">
              -1:00
            </button>

            <button onClick={() => onAdjust(60000)} className="h-10 px-2.5 rounded-xl bg-gray-100 text-gray-600 hover:bg-gray-200 text-xs font-medium" title="+1 Min">
              +1:00
            </button>

            <div className="flex-1" />

            <button onClick={onStop} className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 text-red-500 hover:bg-red-100" title="Stoppen">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
